import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Map, { MapMouseEvent, MapRef, NavigationControl, Popup } from 'react-map-gl';
import { createArrowIcon, MapBoxGlobeMapStyles, MapBoxToken } from '../../../../utils/mapHelpers';
import SignalHoverPopupContent from './popups/SignalHoverPopupContent';
import { TPointSummary } from '../../../../features/main-map/mapTypes';
import { useAppSelector } from '../../../../core/rootHooks';
import { RootState } from '../../../../core/store';

interface IMapProps {
	/**
	 * The children to render inside the map component.
	 */
	children?: React.ReactNode;

	/**
	 * The center of the map in the format { latitude: number, longitude: number }.
	 * If not provided, the map defaults to { latitude: 0, longitude: 0 }.
	 */
	center?: {
		latitude: number;
		longitude: number;
	};

	/**
	 * Whether to load the children directly or wait until the map is loaded.
	 * Defaults to `false`.
	 */
	loadChildrenDirectly?: boolean;
}

/**
 * A wrapper around the react-map-gl Map component. This component is intended to be a drop-in replacement
 * for the Map component from react-map-gl. It provides additional functionality such as:
 * - Adding the necessary images to the map for the arrows to be displayed
 * - Adding a popup when hovering over an interactive layer
 * - Changing the cursor style depending on whether the user is hovering over an interactive layer or not
 * - Providing an onLoad callback to add the necessary images to the map
 * - Providing a prop to load children directly
 *
 * @param {Object} props The props for the Map component
 * @param {Object} [props.center] The center of the map in the format { longitude: number, latitude: number }
 * @param {boolean} [props.loadChildrenDirectly] Whether to load the children directly or not
 * @param {ReactNode} [props.children] The children to render
 */
const MapLibre: FC<IMapProps> = ({ ...props }) => {
	const mapRef = useRef<MapRef>(null);

	const { vesselsLastSignalData } = useAppSelector((state: RootState) => state.vesselsLastSignalSlice);

	const [pointSummary, setPointSummary] = useState<TPointSummary>(null);
	const [cursorStyle, setCursorStyle] = useState<string>(''); // State to track cursor style
	const [viewState, setViewState] = useState({
		zoom: 1,
		...(props.center || { latitude: 0, longitude: 0 }),
	});
	const [mapLoaded, setMapLoaded] = useState(false);
	const [styleLoaded, setStyleLoaded] = useState(false);
	const [layerImagesAdded, setLayerImagesAdded] = useState(false);

	const [settings] = useState({
		scrollZoom: true,
		boxZoom: true,
		dragRotate: false,
		dragPan: true,
		keyboard: true,
		doubleClickZoom: true,
		touchZoomRotate: false,
		touchPitch: true,
	});

	const onMouseMove = useCallback(
		(event: MapMouseEvent) => {
			const feature = event.features && event.features[0];
			if (feature && feature.properties) {
				setCursorStyle('default');

				if (feature.source === 'polygon') {
					setPointSummary({
						type: feature.source,
						name: feature.properties.name,
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				} else if (feature.source === 'points') {
					setCursorStyle('pointer');
					setPointSummary({
						type: feature.source,
						name: `${feature.properties.imoNumber} - ${feature.properties.vesselName}`,
						longitude: feature.properties.longitude,
						latitude: feature.properties.latitude,
					});
				} else if (feature.source === 'lines') {
					setCursorStyle('');
				} else if (feature.source === 'stoppages') {
					setPointSummary({
						type: feature.source,
						name: feature.properties.vesselActivity,
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				} else if (feature.source === 'portCalls') {
					setPointSummary({
						type: feature.source,
						name: feature.properties.portActivity || 'Unclassified',
						longitude: event.lngLat.lng,
						latitude: event.lngLat.lat,
					});
				}
			} else {
				setCursorStyle(''); // Reset cursor when not hovering over an interactive layer
			}
		},
		[setCursorStyle, setPointSummary],
	);

	const onMouseLeave = useCallback(() => {
		setCursorStyle('');
		setPointSummary(null);
	}, [setCursorStyle, setPointSummary]);

	const addColoredArrows = (map: mapboxgl.Map, colors: string[]) => {
		colors.forEach((color) => {
			if (!map.hasImage(`arrow-${color}`)) {
				const canvas = createArrowIcon(color);
				map.addImage(`arrow-${color}`, canvas, { pixelRatio: 2 });
			}
		});
	};

	const addArrows = useCallback(() => {
		if (mapRef.current && vesselsLastSignalData && vesselsLastSignalData.features && vesselsLastSignalData.features.length > 0) {
			const map = mapRef.current.getMap();
			if (map && vesselsLastSignalData && vesselsLastSignalData.features && vesselsLastSignalData.features.length > 0) {
				const uniqueColors = [...new Set(vesselsLastSignalData!.features.map((f) => f.properties!.color))];
				addColoredArrows(map, uniqueColors);
				setLayerImagesAdded(true);
			}

			// add arrow icon for voyage direction points
			if (!map.hasImage('arrow-icon')) {
				const canvas = createArrowIcon('#00355f');
				map.addImage('arrow-icon', canvas, { sdf: false });
				setLayerImagesAdded(true);
			}

			if (!map.hasImage('arrow-icon-long')) {
				const canvas = createArrowIcon('#00355f', 64, 128);
				map.addImage('arrow-icon-long', canvas, { sdf: false });
				setLayerImagesAdded(true);
			}
		}
	}, [vesselsLastSignalData, addColoredArrows, mapRef, setLayerImagesAdded]);

	useEffect(() => {
		const map = mapRef.current?.getMap();

		const handleStyleDataLoading = () => {
			// Style is starting to load - set to false
			setStyleLoaded(false);
			setLayerImagesAdded(false); // Reset this too since images will be lost
		};

		const handleStyleData = () => {
			// Style has finished loading
			if (map && map.isStyleLoaded()) {
				setStyleLoaded(true);
				addArrows(); // Add arrows when style is fully loaded
			}
		};

		const handleLoad = () => {
			setMapLoaded(true);
			if (map && map.isStyleLoaded()) {
				setStyleLoaded(true);
			}
		};

		if (map) {
			map.on('load', handleLoad);
			map.on('styledataloading', handleStyleDataLoading);
			map.on('styledata', handleStyleData);
		}

		return () => {
			if (map) {
				map.off('load', handleLoad);
				map.off('styledataloading', handleStyleDataLoading);
				map.off('styledata', handleStyleData);
			}
		};
	}, [addArrows]);

	useEffect(() => {
		if (
			mapLoaded &&
			styleLoaded &&
			vesselsLastSignalData &&
			vesselsLastSignalData.features &&
			vesselsLastSignalData.features.length > 0
		) {
			addArrows();
		}
	}, [vesselsLastSignalData, mapLoaded, addArrows, styleLoaded]);

	useEffect(() => {
		setViewState({
			...viewState,
			...(props.center || { latitude: 0, longitude: 0 }),
		});
	}, [props.center, setViewState]);

	return (
		<Map
			ref={mapRef}
			{...settings}
			{...viewState}
			style={{ width: '100%', height: '100%' }}
			mapStyle={MapBoxGlobeMapStyles.Msi_Day}
			attributionControl={false}
			antialias
			onMove={(event) => setViewState(event.viewState)}
			interactiveLayerIds={['points', 'stoppages', 'polygons', 'lines', 'portCalls']} // Specify which layer is interactive
			cursor={cursorStyle}
			onMouseMove={onMouseMove}
			onMouseLeave={onMouseLeave}
			mapboxAccessToken={MapBoxToken}
			onLoad={() => {
				// Initial load check - the useEffect above will handle ongoing style changes
				const map = mapRef.current?.getMap();
				if (map) {
					if (map.loaded()) {
						setMapLoaded(true);
					}

					if (map.isStyleLoaded()) {
						setStyleLoaded(true);
					}
				}
			}}
			onStyleData={() => {
				// This fires when style loading completes OR when style changes
				const map = mapRef.current?.getMap();
				if (map && map.isStyleLoaded()) {
					setStyleLoaded(true);
				}
			}}
			onData={() => {
				// This fires when any data loads - use to check style load state
				const map = mapRef.current?.getMap();
				if (map && map.isStyleLoaded()) {
					setStyleLoaded(true);
				}
			}}
			logoPosition="bottom-right"
			projection={{
				name: 'mercator',
			}}
		>
			{pointSummary && (
				<Popup
					longitude={pointSummary.longitude}
					latitude={pointSummary.latitude}
					closeButton={false}
					closeOnClick={false}
					onClose={() => setPointSummary(null)}
					anchor="bottom"
					focusAfterOpen
				>
					<SignalHoverPopupContent info={pointSummary} />
				</Popup>
			)}

			{((mapLoaded && styleLoaded && layerImagesAdded) || (styleLoaded && props.loadChildrenDirectly)) && props.children}

			<NavigationControl position="bottom-right" showCompass={false} />
		</Map>
	);
};

export default MapLibre;
